import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ChevronRight } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import StrategyDisplay from "./alts-allocation/StrategyDisplay";
import AltAllocationDisplay from "./alts-allocation/AltAllocationDisplay";
import { useInvestmentStrategy } from "./investment-strategy/InvestmentStrategyContext";
import { usePortfolioSize } from "./portfolio/PortfolioSizeContext";
import { AltsAllocation } from "@/types/investment";
import { supabase } from "@/integrations/supabase/client";
import PortfolioDisplay from "./potential-returns/PortfolioDisplay";
import ReturnInputs from "./potential-returns/ReturnInputs";
import PerformanceDataTable from "./potential-returns/PerformanceDataTable";

interface PotentialReturnsStepProps {
  onContinue: () => void;
}

const PotentialReturnsStep = ({ onContinue }: PotentialReturnsStepProps) => {
  const { selectedStrategy } = useInvestmentStrategy();
  const { portfolioSize } = usePortfolioSize();
  const [annualizedReturn, setAnnualizedReturn] = useState("0.00");
  const [projectedAnnualizedReturn, setProjectedAnnualizedReturn] = useState("0.00");
  const [altAllocations, setAltAllocations] = useState<AltsAllocation | null>(null);
  const [performanceData, setPerformanceData] = useState<any[]>([]);
  
  useEffect(() => {
    const fetchPerformanceData = async () => {
      const { data, error } = await supabase
        .from('performance_indexes')
        .select('*')
        .order('date', { ascending: true });
      
      if (error) {
        console.error('Error fetching performance data:', error);
      } else {
        setPerformanceData(data);
      }
    };

    fetchPerformanceData();
  }, []);
  
  useEffect(() => {
    const savedAllocations = localStorage.getItem('altAllocations');
    if (savedAllocations) {
      console.log('Loading alt allocations from localStorage:', savedAllocations);
      try {
        const parsedAllocations = JSON.parse(savedAllocations);
        console.log('Parsed allocations:', parsedAllocations);
        setAltAllocations(parsedAllocations);
      } catch (error) {
        console.error('Error parsing alt allocations:', error);
      }
    } else {
      console.log('No alt allocations found in localStorage');
    }
  }, []);

  const handleAnnualizedReturnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d*\.?\d{0,2}$/.test(value)) {
      setAnnualizedReturn(value);
    }
  };

  const handleProjectedAnnualizedReturnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d*\.?\d{0,2}$/.test(value)) {
      setProjectedAnnualizedReturn(value);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-navy-dark font-medium">
        Explore potential rates of return based on your allocations.
      </p>
      
      <Accordion type="single" collapsible>
        <AccordionItem value="learn-more">
          <AccordionTrigger className="text-navy-dark">
            <span className="font-medium">Development Tests</span>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4">
              <PortfolioDisplay portfolioSize={portfolioSize} />

              <ReturnInputs
                annualizedReturn={annualizedReturn}
                projectedAnnualizedReturn={projectedAnnualizedReturn}
                onAnnualizedReturnChange={handleAnnualizedReturnChange}
                onProjectedAnnualizedReturnChange={handleProjectedAnnualizedReturnChange}
              />

              {selectedStrategy && (
                <StrategyDisplay selectedStrategy={selectedStrategy} />
              )}

              <div className="mt-4">
                <AltAllocationDisplay altAllocations={altAllocations} />
              </div>

              <PerformanceDataTable performanceData={performanceData} />
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <div className="space-y-6">
        <div className="mt-8 bg-white p-6 rounded-lg shadow-sm">
          <h3 className="text-lg font-semibold text-navy-dark mb-4">
            Potential Returns
          </h3>
          <div className="h-64 bg-gray-100 rounded-lg flex items-center justify-center">
            Potential Returns Chart Placeholder
          </div>
        </div>

        <div className="pt-6">
          <Button size="lg" className="w-full max-w-md" onClick={onContinue}>
            Continue
            <ChevronRight className="ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PotentialReturnsStep;
